// Mark Non-Applicable Panel
// Pull-out panel for marking compliance requirements as non-applicable

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CalendarIcon, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ComplianceItemCard } from './compliance-item-card';
import { useNavigate } from 'react-router-dom';

// Form validation schema
const formSchema = z.object({
  reason: z.enum([
    'AssetDisposed',
    'RegulationNotApplicable',
    'TemporaryExemption',
    'PermanentExemption',
    'Other',
  ], {
    message: 'Exemption reason is required',
  }),
  notes: z.string().min(20, 'Detailed explanation must be at least 20 characters'),
  effectiveDate: z.date({
    message: 'Effective date is required',
  }),
  expiryDate: z.date().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ComplianceItem {
  id: number;
  assetId?: string;
  assetName: string;
  assetCode?: string;
  regimeCode?: string;
  regimeName: string;
  complianceCheckId?: number;
}

interface MarkNonApplicablePanelProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly complianceItem?: ComplianceItem;
  readonly onSubmit: (data: FormValues) => Promise<void>;
}

const reasonLabels: Record<string, string> = {
  AssetDisposed: 'Asset Disposed',
  RegulationNotApplicable: 'Regulation Not Applicable',
  TemporaryExemption: 'Temporary Exemption',
  PermanentExemption: 'Permanent Exemption',
  Other: 'Other',
};

export function MarkNonApplicablePanel({
  open,
  onOpenChange,
  complianceItem,
  onSubmit,
}: Readonly<MarkNonApplicablePanelProps>) {
  const navigate = useNavigate();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: undefined,
      notes: '',
      effectiveDate: new Date(),
      expiryDate: undefined,
    },
  });

  const selectedReason = form.watch('reason');
  const showExpiryDate = selectedReason === 'TemporaryExemption';

  const handleReasonChange = (value: string) => {
    form.setValue('reason', value as FormValues['reason']);
    if (value !== 'TemporaryExemption') {
      form.setValue('expiryDate', undefined);
    }
  };

  const handleSubmit = async (data: FormValues) => {
    try {
      await onSubmit(data);
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error marking as non-applicable:', error);
    }
  };

  const handleNavigateToAsset = (assetId: string) => {
    navigate(`/assets/${assetId}`);
  };

  const handleNavigateToRule = (ruleId: number) => {
    navigate(`/compliance/regulations?ruleId=${ruleId}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[540px] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 bg-white dark:bg-black border-b">
          <SheetTitle className="text-lg font-semibold">Mark as Non-Applicable</SheetTitle>
          <SheetDescription>
            Mark this compliance requirement as non-applicable
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 bg-muted/30">
          <Form {...form}>
            <form id="mark-non-applicable-form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 pb-6 pt-6">
              {/* Compliance Item Info */}
              {complianceItem && (
                <ComplianceItemCard
                  assetId={complianceItem.assetId}
                  assetName={complianceItem.assetName}
                  assetCode={complianceItem.assetCode}
                  regimeCode={complianceItem.regimeCode || 'N/A'}
                  regimeName={complianceItem.regimeName}
                  complianceCheckId={complianceItem.complianceCheckId}
                  onNavigateToAsset={handleNavigateToAsset}
                  onNavigateToRule={handleNavigateToRule}
                />
              )}

              {/* Warning Alert */}
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Marking this requirement as non-applicable will remove it from compliance tracking. 
                  Ensure you have proper authorization and documentation.
                </AlertDescription>
              </Alert>

              {/* Exemption Reason */}
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">Exemption Reason *</FormLabel>
                    <Select onValueChange={handleReasonChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="bg-white dark:bg-gray-950">
                          <SelectValue placeholder="Select reason" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(reasonLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-xs">
                      Why is this requirement no longer applicable?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                {/* Effective Date */}
                <FormField
                  control={form.control}
                  name="effectiveDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="font-semibold">Effective Date *</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                'w-full pl-3 text-left font-normal bg-white dark:bg-gray-950',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              {field.value ? (
                                format(field.value, 'PPP')
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormDescription className="text-xs">
                        When does this exemption start?
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Expiry Date (only for temporary exemptions) */}
                {showExpiryDate && (
                  <FormField
                    control={form.control}
                    name="expiryDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="font-semibold">Expiry Date *</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal bg-white dark:bg-gray-950',
                                  !field.value && 'text-muted-foreground'
                                )}
                              >
                                {field.value ? (
                                  format(field.value, 'PPP')
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => {
                                const effectiveDate = form.getValues('effectiveDate');
                                return effectiveDate ? date <= effectiveDate : date < new Date();
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription className="text-xs">
                          When does this exemption end?
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {/* Detailed Explanation */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">Detailed Explanation *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Provide a detailed explanation for marking this requirement as non-applicable..."
                        className="min-h-[120px] bg-white dark:bg-gray-950"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Include authorization details and supporting documentation (minimum 20 characters)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>

        <SheetFooter className="border-t bg-white dark:bg-black px-6 py-4 mt-auto">
          <div className="flex gap-3 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="mark-non-applicable-form"
              variant="destructive"
              className="flex-1"
            >
              Mark Non-Applicable
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
